import { useEffect, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet.jsx';
import { acceptPeopleSuggestion } from '@/services/people-suggestions.service.js';

// THE PEOPLE SUGGESTION SHEET (people-create-door R26, PLAN-ROUND3 section 2, Q14). Broker
// only; nothing here reaches a client. A suggestion is a CLAIM a door heard, never verified:
//   person_new     "New person": the heard name, phone and email and everything else the
//                  door heard (evidence.heard). Each identity key is a checkbox, and a
//                  ticked box IS the confirm echo the server asks for; an unticked key is
//                  withheld (fields[k] = null), never written. Where the person belongs is
//                  the one proposed account when there is exactly one, else David picks (an
//                  account, a lead, a carrier contact, not on an account yet): never silently
//                  defaulted, Accept stays off until it is chosen. A 409 match_found shows
//                  the match and offers Keep separate (confirm_new).
//   person_update  one accept per proposed field, each with its own echo.
// In zoho mode the server answers 409 people_off: said once, as the server's sentence.

const IDENTITY_KEYS = ['crm_contact_id', 'email', 'phone'];
const KEY_WORD = { crm_contact_id: 'Contact id', email: 'Email', phone: 'Phone' };
const AGENCY_PLACES = [
  ['lead', 'A lead'],
  ['carrier_contact', 'A carrier contact'],
  ['unlinked', 'Not on an account yet'],
];
const ROLES = [
  ['contact', 'Contact'],
  ['primary_contact', 'Primary contact'],
  ['named_insured', 'Named insured'],
  ['pilot', 'Pilot'],
  ['member', 'Member'],
  ['signer', 'Signer'],
  ['payer', 'Payer'],
];
const HEARD_WORD = {
  name: 'Name', phone: 'Phone', email: 'Email', certificates: 'Certificates', ratings: 'Ratings',
  total_hours: 'Total hours', hours_last_12_months: 'Hours, last 12 months', retractable_hours: 'Retractable hours',
  tailwheel_hours: 'Tailwheel hours', tail_number: 'Tail number', make_model: 'Make and model', year: 'Year', hull_value: 'Hull value',
};

const field = 'w-full rounded border border-border bg-bg px-2 py-1.5 text-[13px] text-text';
const primary = 'inline-flex min-h-[36px] items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50';
const quiet = 'inline-flex min-h-[36px] items-center gap-1 rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-[12.5px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50';

const shown = (v) => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v));

// One sentence per refusal: a zoho-mode refusal is the switch's answer, not a failure.
export function sayAcceptError(err) {
  const code = err?.body?.error;
  const said = err?.body?.message || code || err?.message || 'Could not accept';
  if (code === 'people_off' || code === 'people_not_authoritative') toast.info(said);
  else toast.error(said);
}

function sayAccepted(r, line) {
  if (r && r.suggestion && r.suggestion.resolution) toast.success(line);
  else toast.info('The answer was sent; it could not be read back. Read the queue again.');
  for (const w of r.warnings || []) toast.info(typeof w === 'string' ? w : (w.message || w.code));
}

function Heard({ heard }) {
  const rows = Object.entries(heard || {}).filter(([, v]) => shown(v).trim());
  if (!rows.length) return <p className="text-[12px] text-text-dim">Nothing else was heard.</p>;
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-[12px]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-text-dim">{HEARD_WORD[k] || k.replace(/_/g, ' ')}</dt>
          <dd className="break-words text-text">{shown(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function NewPersonForm({ s, onDone }) {
  const proposed = s.proposed || {};
  const accounts = (s.proposed_accounts || []).filter((a) => a && !a.missing);
  const unread = (s.proposed_accounts || []).filter((a) => a && a.missing);
  const keys = IDENTITY_KEYS.filter((k) => proposed[k]);
  const [first, setFirst] = useState(proposed.first_name || '');
  const [last, setLast] = useState(proposed.last_name || '');
  const [ticked, setTicked] = useState({});
  const [where, setWhere] = useState(accounts.length === 1 ? `account:${accounts[0].id}` : '');
  const [role, setRole] = useState('contact');
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);

  function body(keepSeparate) {
    const fields = {};
    const confirm = {};
    for (const k of keys) {
      if (ticked[k]) confirm[k] = proposed[k];
      else fields[k] = null;
    }
    if (first.trim() !== (proposed.first_name || '')) fields.first_name = first.trim() || null;
    if (last.trim() !== (proposed.last_name || '')) fields.last_name = last.trim() || null;
    const place = where.startsWith('account:') ? { account_id: where.slice('account:'.length), role } : { agency_role: where };
    return { fields, confirm, ...place, ...(keepSeparate ? { confirm_new: true } : {}) };
  }

  async function send(keepSeparate = false) {
    if (!where) return;
    setBusy(true);
    try {
      const r = await acceptPeopleSuggestion(s.id, body(keepSeparate));
      sayAccepted(r, 'New person recorded.');
      onDone?.();
    } catch (err) {
      if (err?.body?.error === 'match_found') setMatch(err.body.match || {});
      else sayAcceptError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 space-y-4">
      <section>
        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-dim">Heard</h4>
        <Heard heard={s.evidence?.heard} />
      </section>
      <section className="grid grid-cols-2 gap-2">
        <label className="text-[12px] text-text-muted">First name<input value={first} onChange={(e) => setFirst(e.target.value)} className={field} /></label>
        <label className="text-[12px] text-text-muted">Last name<input value={last} onChange={(e) => setLast(e.target.value)} className={field} /></label>
      </section>
      <section>
        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-dim">Write onto the new person</h4>
        {keys.length === 0 ? <p className="text-[12px] text-text-dim">No Contact id, email or phone was heard.</p> : null}
        {keys.map((k) => (
          <label key={k} className="flex items-center gap-2 py-0.5 text-[13px] text-text">
            <input type="checkbox" checked={!!ticked[k]} onChange={(e) => setTicked((t) => ({ ...t, [k]: e.target.checked }))} className="h-4 w-4 accent-[#C62D48]" />
            <span>{KEY_WORD[k]}: {proposed[k]}</span>
          </label>
        ))}
        <p className="mt-1 text-[11px] text-text-dim">A box you leave empty is not written. Heard on a call or in a message, never checked.</p>
      </section>
      <section>
        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-dim">Where they belong</h4>
        {accounts.map((a) => (
          <label key={a.id} className="flex items-center gap-2 py-0.5 text-[13px] text-text">
            <input type="radio" name={`where-${s.id}`} checked={where === `account:${a.id}`} onChange={() => setWhere(`account:${a.id}`)} />
            <span>On the account {a.name || a.id}</span>
          </label>
        ))}
        {unread.map((a) => <p key={a.id} className="text-[12px] text-warning">Account {a.id} could not be read.</p>)}
        {AGENCY_PLACES.map(([v, w]) => (
          <label key={v} className="flex items-center gap-2 py-0.5 text-[13px] text-text">
            <input type="radio" name={`where-${s.id}`} checked={where === v} onChange={() => setWhere(v)} />
            <span>{w}</span>
          </label>
        ))}
        {where.startsWith('account:') ? (
          <label className="mt-1 block text-[12px] text-text-muted">Role on the account
            <select value={role} onChange={(e) => setRole(e.target.value)} className={field}>
              {ROLES.map(([v, w]) => <option key={v} value={v}>{w}</option>)}
            </select>
          </label>
        ) : null}
      </section>
      {match ? (
        <section className="rounded-card border border-border bg-bg px-3 py-2 text-[12.5px] text-text">
          <p className="font-medium text-warning">This may already be someone on file ({match.outcome || 'a match'}).</p>
          {(match.candidates || []).map((c) => <p key={c.person_id} className="text-text-muted">Person {c.person_id}{Array.isArray(c.keys) && c.keys.length ? `, same ${c.keys.join(' and ')}` : ''}</p>)}
          <p className="mt-1 text-[11px] text-text-dim">Close this and tap the person instead, or keep them as two people.</p>
          <button type="button" disabled={busy || !where} onClick={() => send(true)} className={`${quiet} mt-1.5`}>Keep separate</button>
        </section>
      ) : null}
      <div className="flex justify-end">
        <button type="button" disabled={busy || !where} onClick={() => send(false)} className={primary}>{busy ? 'Saving...' : 'Accept as a new person'}</button>
      </div>
    </div>
  );
}

function UpdateForm({ s, onDone }) {
  const changes = s.proposed?.changes || {};
  const groups = Object.keys(changes);
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState(null);
  const who = s.subject_person;

  async function accept(g) {
    setBusy(g);
    try {
      const r = await acceptPeopleSuggestion(s.id, { kind: 'person_update', only: [g], confirm: { [g]: changes[g].proposed } });
      sayAccepted(r, `${KEY_WORD[g] || g} updated.`);
      onDone?.();
    } catch (err) {
      sayAcceptError(err);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-4 space-y-3">
      <p className="text-[12.5px] text-text-muted">
        {who && !who.missing ? `For ${[who.preferred_name || who.first_name, who.last_name].filter(Boolean).join(' ') || `person ${who.id}`}.` : `For person ${s.person_id} (the record could not be read).`}
        {' '}Accepting one change closes this suggestion; a change you do not accept is not written.
      </p>
      {groups.map((g) => (
        <div key={g} className="rounded-card border border-border px-3 py-2">
          <p className="text-[13px] text-text">{KEY_WORD[g] || g}: {changes[g].current || '(none on file)'} to {changes[g].proposed}</p>
          <label className="mt-1 flex items-center gap-2 text-[12.5px] text-text-muted">
            <input type="checkbox" checked={!!ticked[g]} onChange={(e) => setTicked((t) => ({ ...t, [g]: e.target.checked }))} className="h-4 w-4 accent-[#C62D48]" />
            <span>Confirm {changes[g].proposed}</span>
          </label>
          <button type="button" disabled={!!busy || !ticked[g]} onClick={() => accept(g)} className={`${primary} mt-1.5`}>{busy === g ? 'Saving...' : `Accept the ${(KEY_WORD[g] || g).toLowerCase()}`}</button>
        </div>
      ))}
    </div>
  );
}

export default function PersonSuggestionSheet({ open, onOpenChange, suggestion, onDone }) {
  const [key, setKey] = useState(0);
  useEffect(() => { if (open) setKey((k) => k + 1); }, [open]);
  const s = suggestion;
  if (!s) return null;
  const isUpdate = s.kind === 'person_update';
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{isUpdate ? 'Update suggested for a person on file' : 'New person'}</SheetTitle>
          <SheetDescription>Heard by a door, not checked. Nothing is written until you accept.</SheetDescription>
        </SheetHeader>
        {isUpdate ? <UpdateForm key={key} s={s} onDone={onDone} /> : <NewPersonForm key={key} s={s} onDone={onDone} />}
      </SheetContent>
    </Sheet>
  );
}
